if (!customElements.get('estoria-comments')) {
  customElements.define('estoria-comments', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.listeners = new AbortController();
      this.reducedMotion = this.ownerDocument.defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)') || { matches: false };
      const options = { signal: this.listeners.signal };
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('change', event => this.onChange(event), options);
      this.addEventListener('keydown', event => this.onKeydown(event), options);
      for (const type of ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']) {
        this.ownerDocument.addEventListener(type, event => this.onSectionChange(event), options);
      }
      this.ownerDocument.addEventListener('shopify:section:select', event => this.onSectionSelect(event), options);
      this.ownerDocument.addEventListener('shopify:block:select', event => this.onBlockSelect(event), options);
      this.refresh();
    }

    disconnectedCallback() {
      this.listeners?.abort();
      if (this.refreshFrame != null) cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = null;
    }

    get designMode() {
      return this.hasAttribute('data-design-mode') && this.dataset.designMode !== 'false';
    }

    owns(element) {
      return element?.closest?.('estoria-comments') === this;
    }

    elements(selector) {
      return [...this.querySelectorAll(selector)].filter(element => this.owns(element));
    }

    refresh() {
      const previous = this.currentComment;
      this.banks = this.elements('.estoria-comment-bank').map(element => {
        const section = element.closest('.shopify-section');
        return {
          element,
          section: this.contains(section) && section !== this ? section : null,
          handle: element.dataset.productHandle,
          title: element.dataset.productTitle,
          articles: [...element.querySelectorAll('[data-comment]')].filter(article => (
            this.owns(article) && article.closest('.estoria-comment-bank') === element
          )),
        };
      });
      this.comments = this.banks.flatMap(bank => bank.articles.map(article => ({ article, bank, handle: bank.handle })));
      const products = new Map(this.banks.map(bank => [bank.handle, bank.title]));
      if (!products.has(this.filterHandle)) this.filterHandle = '';
      this.filter = this.elements('select[data-comment-filter]')[0];
      if (this.filter) {
        const options = [['', this.filter.dataset.allLabel], ...products].map(([value, title]) => {
          const option = this.ownerDocument.createElement('option');
          option.value = value;
          option.textContent = title;
          return option;
        });
        this.filter.replaceChildren(...options);
        this.filter.value = this.filterHandle;
      }
      this.filtered = this.comments.filter(comment => !this.filterHandle || comment.handle === this.filterHandle);
      this.index = Math.max(0, this.filtered.findIndex(comment => previous && (
        comment.article === previous.article || (
          comment.handle === previous.handle && previous.article.dataset.commentId &&
          comment.article.dataset.commentId === previous.article.dataset.commentId
        )
      )));
      this.render();
    }

    render() {
      const focused = this.ownerDocument.activeElement;
      this.currentComment = this.filtered[this.index];
      const active = this.currentComment?.article;
      for (const bank of this.banks) {
        const bankActive = bank === this.currentComment?.bank;
        bank.element.hidden = !bankActive;
        if (bank.section) bank.section.hidden = !bankActive;
        for (const article of bank.articles) {
          article.hidden = !bankActive;
          article.tabIndex = article === active ? 0 : -1;
          if (article === active) article.setAttribute('aria-current', 'true');
          else article.removeAttribute('aria-current');
        }
      }
      this.hidden = !this.comments.length && !this.designMode;
      for (const controls of this.elements('[data-comment-controls]')) controls.hidden = !this.comments.length;
      for (const button of this.elements('button[data-comment-prev], button[data-comment-next]')) {
        button.disabled = this.filtered.length < 2;
      }
      const current = active ? this.index + 1 : 0;
      const total = this.filtered.length;
      for (const counter of this.elements('[data-comment-current]')) counter.textContent = String(current);
      for (const counter of this.elements('[data-comment-total]')) counter.textContent = String(total);
      for (const status of this.elements('[data-comment-status]')) {
        status.textContent = (status.dataset.message || '')
          .replaceAll('[current]', String(current)).replaceAll('[total]', String(total));
      }
      if (active && focused !== active && this.comments.some(comment => comment.article === focused)) {
        active.focus({ preventScroll: true });
      }
    }

    move(direction) {
      if (this.filtered.length < 2) return;
      this.index = (this.index + direction + this.filtered.length) % this.filtered.length;
      this.render();
      this.scrollCurrent();
    }

    scrollCurrent() {
      const card = this.currentComment?.article;
      if (card?.closest('estoria-review-loop')?.scrollToCard?.(card)) return;
      this.currentComment?.article.scrollIntoView({ behavior: this.reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    }

    onClick(event) {
      const button = event.target.closest?.('button[data-comment-prev], button[data-comment-next]');
      if (!this.owns(button) || button.disabled) return;
      event.preventDefault();
      this.move(button.hasAttribute('data-comment-prev') ? -1 : 1);
    }

    onChange(event) {
      if (event.target !== this.filter) return;
      this.filterHandle = this.filter.value;
      this.currentComment = null;
      this.refresh();
    }

    onKeydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const target = event.target;
      if (!this.owns(target) || target.closest('input, select, textarea') || target.isContentEditable) return;
      const button = target.closest('button[data-comment-prev], button[data-comment-next]');
      if (target !== this.currentComment?.article && (!this.owns(button) || button.disabled)) return;
      if (this.filtered.length < 2) return;
      event.preventDefault();
      this.move(event.key === 'ArrowLeft' ? -1 : 1);
    }

    onSectionChange(event) {
      if (!this.owns(event.target)) return;
      if (event.type !== 'shopify:section:unload') {
        this.refresh();
        return;
      }
      if (this.refreshFrame != null) return;
      this.refreshFrame = requestAnimationFrame(() => {
        this.refreshFrame = null;
        if (this.isConnected) this.refresh();
      });
    }

    onSectionSelect(event) {
      if (!this.designMode || !this.owns(event.target)) return;
      const bank = this.elements('.estoria-comment-bank').find(element => element.closest('.shopify-section') === event.target);
      if (!bank) return;
      const articles = this.elements('[data-comment]').filter(article => article.closest('.estoria-comment-bank') === bank);
      const article = articles.includes(this.currentComment?.article) ? this.currentComment.article : articles[0];
      if (!article) return;
      this.filterHandle = '';
      this.currentComment = { article, handle: bank.dataset.productHandle };
      this.refresh();
      article.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
    }

    onBlockSelect(event) {
      if (!this.designMode || !this.owns(event.target) || !event.detail?.blockId) return;
      const article = this.elements('[data-comment]').find(comment => comment.dataset.blockId === event.detail.blockId);
      const bank = article?.closest('.estoria-comment-bank');
      if (!bank) return;
      this.filterHandle = '';
      this.currentComment = { article, handle: bank.dataset.productHandle };
      this.refresh();
      article.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
    }
  });
}
